import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Card, CardContent, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Tooltip, Alert,
} from '@mui/material';
import {
  Paid as CostIcon, DataUsage as TokenIcon, Business as ClinicIcon,
  Today as TodayIcon, ScienceOutlined as ProtocolIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Friendly labels for the protocol_* operation tags written by the backend.
const OP_LABELS = {
  protocol_draft: 'Draft', protocol_amend: 'Amend', protocol_expand: 'Expand',
  protocol_critique: 'Critique', protocol_explain: 'Explain', protocol_reconcile: 'Reconcile',
  protocol_format: 'Format', protocol_capture: 'Capture (recording)',
  protocol_checklist_extract: 'Extract visit questions',
  protocol_live_check: 'Live question check (in-visit)',
};

const money = (n) => {
  const v = Number(n || 0);
  if (v === 0) return '$0.00';
  if (v < 0.01) return `$${v.toFixed(4)}`;   // sub-cent shown honestly
  return `$${v.toFixed(2)}`;
};
const compact = (n) => Intl.NumberFormat('en', { notation: 'compact' }).format(n || 0);

function StatCard({ icon, label, value, sub, color = '#0f172a' }) {
  return (
    <Card elevation={0} sx={{ height: '100%', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 1 }}>
          {icon}
          <Typography variant="body2" fontWeight={600}>{label}</Typography>
        </Box>
        <Typography variant="h4" fontWeight={800} sx={{ color }}>{value}</Typography>
        {sub && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// Dependency-free daily bar chart (admin-portal has no chart lib).
function DailyBars({ daily }) {
  const max = Math.max(...daily.map((d) => d.cost), 0.000001);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 140, mt: 1 }}>
      {daily.map((d) => (
        <Tooltip key={d.date} title={`${d.date}: ${money(d.cost)} · ${compact(d.tokens)} tok`} arrow>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
            <Box sx={{
              height: `${Math.max((d.cost / max) * 100, d.cost > 0 ? 4 : 0)}%`,
              bgcolor: d.cost > 0 ? '#0d9488' : 'rgba(15,23,42,0.08)',
              borderRadius: '4px 4px 0 0', minHeight: d.cost > 0 ? 4 : 2,
              transition: 'height .2s',
            }} />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

// Horizontal share bar used in the per-clinic and per-intent tables.
function ShareBar({ value, max, color = '#0d9488' }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <Box sx={{ position: 'relative', height: 8, borderRadius: 4, bgcolor: 'rgba(15,23,42,0.06)' }}>
      <Box sx={{ position: 'absolute', inset: 0, width: `${pct}%`, bgcolor: color, borderRadius: 4 }} />
    </Box>
  );
}

export default function ProtocolCost() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/v1/superadmin/protocol-usage`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load protocol cost'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!data) return <Typography>No data.</Typography>;

  const cum = data.cumulative || {};
  const mtd = data.month_to_date || {};
  const today = data.today || {};
  const maxClinic = Math.max(...(data.by_clinic || []).map((c) => c.cost), 0.000001);
  const maxOp = Math.max(...(data.by_operation || []).map((o) => o.cost), 0.000001);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <ProtocolIcon sx={{ color: '#0d9488' }} />
        <Typography variant="h4" fontWeight={800}>Protocol Development — Cost</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        AI spend for Protocol Development only ({data.model}). Estimates from token usage × Pro rates;
        Google billing is authoritative.
      </Typography>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<CostIcon fontSize="small" />} label="Total spend (all time)" value={money(cum.cost)} color="#0d9488"
            sub={`${cum.calls || 0} AI calls`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TokenIcon fontSize="small" />} label="Total tokens" value={compact(cum.tokens)}
            sub={`${compact(cum.audio_tokens)} audio`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<ClinicIcon fontSize="small" />} label="Active clinics" value={data.active_clinics || 0}
            sub="with protocol spend" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<TodayIcon fontSize="small" />} label="Month to date" value={money(mtd.cost)}
            sub={`Today ${money(today.cost)}`} />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
        <Typography fontWeight={700} sx={{ mb: 0.5 }}>Spend over the last 30 days</Typography>
        <DailyBars daily={data.daily || []} />
      </Paper>

      <Grid container spacing={3}>
        {/* Per-clinic breakdown */}
        <Grid item xs={12} md={7}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Spend by clinic</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Clinic</TableCell>
                  <TableCell sx={{ width: '30%' }}>Share</TableCell>
                  <TableCell align="right">Tokens</TableCell>
                  <TableCell align="right">Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.by_clinic || []).length === 0 ? (
                  <TableRow><TableCell colSpan={4}>No protocol spend yet.</TableCell></TableRow>
                ) : data.by_clinic.map((c) => (
                  <TableRow key={c.clinic_id || c.clinic} hover>
                    <TableCell><Typography fontWeight={600} variant="body2">{c.clinic}</Typography></TableCell>
                    <TableCell><ShareBar value={c.cost} max={maxClinic} /></TableCell>
                    <TableCell align="right">{compact(c.tokens)}</TableCell>
                    <TableCell align="right"><Typography fontWeight={700} variant="body2">{money(c.cost)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Per-intent breakdown */}
        <Grid item xs={12} md={5}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Spend by action</Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            {(data.by_operation || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No protocol spend yet.</Typography>
            ) : data.by_operation.map((o) => (
              <Box key={o.operation} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{OP_LABELS[o.operation] || o.operation}</Typography>
                  <Typography variant="body2" color="text.secondary">{money(o.cost)} · {o.calls}×</Typography>
                </Box>
                <ShareBar value={o.cost} max={maxOp} color="#6366f1" />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }}>
        Dollar figures are visible to platform super admins only — clinics, clinic admins and users never see cost.
      </Alert>
    </Box>
  );
}
